/**
 * AITextHelper — wraps a textarea and adds a ✨ AI improvement button.
 * On click: sends current text to POST /api/ai/improve-text
 * Returns improved version; user can Accept or Keep original.
 */

import React, { useState } from "react";
import { api } from "../../services/api";

interface Props {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  context: string;
  tone?: "professional" | "friendly" | "urgent";
  rows?: number;
  className?: string;
}

const AITextHelper: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  context,
  tone = "professional",
  rows = 3,
  className,
}) => {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImprove = async () => {
    if (!value.trim()) return;
    setLoading(true);
    setError(null);
    setSuggestion(null);
    try {
      const res = await api.ai.improveText(value, context, tone);
      setSuggestion(res.improved);
    } catch (e) {
      setError("AI unavailable");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = () => {
    if (!suggestion) return;
    const syntheticEvent = {
      target: { value: suggestion },
    } as React.ChangeEvent<HTMLTextAreaElement>;
    onChange(syntheticEvent);
    setSuggestion(null);
  };

  const handleKeep = () => setSuggestion(null);

  return (
    <div style={{ position: "relative" }}>
      <textarea
        className={className}
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{ width: "100%", paddingRight: "2.5rem", boxSizing: "border-box" }}
      />

      <button
        type="button"
        title="Improve with AI"
        onClick={handleImprove}
        disabled={loading || !value.trim()}
        style={{
          position: "absolute",
          top: "0.4rem",
          right: "0.4rem",
          background: "transparent",
          border: "none",
          cursor: loading || !value.trim() ? "not-allowed" : "pointer",
          fontSize: "1rem",
          lineHeight: 1,
          opacity: loading || !value.trim() ? 0.4 : 0.85,
          padding: "0.15rem 0.3rem",
          borderRadius: "4px",
          transition: "opacity 0.2s",
        }}
      >
        {loading ? "⏳" : "✨"}
      </button>

      {error && (
        <p style={{ fontSize: "0.75rem", color: "var(--danger, #ef4444)", marginTop: "0.25rem" }}>
          {error}
        </p>
      )}

      {suggestion && (
        <div
          style={{
            marginTop: "0.5rem",
            padding: "0.75rem",
            background: "rgba(255, 214, 165, 0.12)",
            border: "1px solid rgba(255, 214, 165, 0.35)",
            borderRadius: "6px",
            fontSize: "0.875rem",
          }}
        >
          <p style={{ margin: "0 0 0.5rem", color: "var(--text-muted, #9ca3af)", fontSize: "0.75rem" }}>
            ✨ AI suggestion:
          </p>
          <p style={{ margin: "0 0 0.75rem", lineHeight: 1.5 }}>{suggestion}</p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              onClick={handleAccept}
              style={{
                padding: "0.3rem 0.75rem",
                background: "rgba(255, 214, 165, 0.25)",
                border: "1px solid rgba(255, 214, 165, 0.5)",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: "#ffd6a5",
              }}
            >
              Accept
            </button>
            <button
              type="button"
              onClick={handleKeep}
              style={{
                padding: "0.3rem 0.75rem",
                background: "transparent",
                border: "1px solid rgba(156, 163, 175, 0.4)",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "0.8rem",
                color: "var(--text-muted, #9ca3af)",
              }}
            >
              Keep original
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITextHelper;
